import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  AlertTriangle, 
  Shield, 
  Calendar as CalendarIcon, 
  ChevronRight, 
  ChevronLeft,
  CheckCircle,
  XCircle,
  ArrowLeft,
  Loader2,
  Info,
  AlertCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../contexts/AuthContext';
import PortalLayout from '../components/PortalLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Steps in the cancellation flow
const STEPS = {
  REASON: 'reason',
  TIMING: 'timing',
  RETENTION: 'retention',
  DISCLOSURES: 'disclosures',
  CONFIRMATION: 'confirmation',
  COMPLETE: 'complete'
};

export default function CancellationPage() {
  const { policyNumber } = useParams();
  const navigate = useNavigate();
  const { getToken } = useAuth();
  
  // State
  const [currentStep, setCurrentStep] = useState(STEPS.REASON);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  
  // Policy data
  const [policyInfo, setPolicyInfo] = useState(null);
  const [reasons, setReasons] = useState([]);
  
  // Form data
  const [selectedReason, setSelectedReason] = useState('');
  const [reasonDetails, setReasonDetails] = useState('');
  const [effectiveDate, setEffectiveDate] = useState('');
  const [cancellationType, setCancellationType] = useState('policyholder_request');
  
  // Retention data
  const [retentionData, setRetentionData] = useState(null);
  const [retentionAction, setRetentionAction] = useState('');
  
  // Refund estimate
  const [refundEstimate, setRefundEstimate] = useState(null);
  
  // Disclosures
  const [disclosures, setDisclosures] = useState({
    coverage_ends_acknowledged: false,
    no_future_coverage_acknowledged: false,
    refund_terms_acknowledged: false
  });
  
  // Confirmation result
  const [cancellationResult, setCancellationResult] = useState(null);

  // Fetch policy info on mount
  useEffect(() => {
    fetchPolicyInfo();
  }, [policyNumber]);

  const fetchPolicyInfo = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = getToken();
      const response = await fetch(`${API_URL}/api/cancellation/policy/${policyNumber}`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to load policy information');
      }
      
      setPolicyInfo(data);
      setReasons(data.reasons || []);
      
      // Set default effective date to today
      const today = new Date().toISOString().split('T')[0];
      setEffectiveDate(today);
      
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const validateDate = async () => {
    try {
      setProcessing(true);
      
      const response = await fetch(`${API_URL}/api/cancellation/validate-date`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          policy_number: policyNumber,
          reason_code: selectedReason,
          reason_details: reasonDetails,
          effective_date: effectiveDate,
          cancellation_type: cancellationType
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Invalid cancellation date');
      }
      
      setRefundEstimate(data.refund_estimate);
      return true;
      
    } catch (err) {
      setError(err.message);
      return false;
    } finally {
      setProcessing(false);
    }
  };

  const fetchRetentionOffer = async () => {
    try {
      setProcessing(true);
      
      const response = await fetch(`${API_URL}/api/cancellation/retention`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          policy_number: policyNumber,
          reason_code: selectedReason,
          reason_details: reasonDetails,
          effective_date: effectiveDate,
          cancellation_type: cancellationType
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to load retention offer');
      }
      
      setRetentionData(data);
      setRefundEstimate(data.refund_estimate);
      
    } catch (err) {
      setError(err.message);
    } finally {
      setProcessing(false);
    }
  };

  const handleRetentionResponse = async (action) => {
    try {
      setProcessing(true);
      setRetentionAction(action);
      
      const response = await fetch(`${API_URL}/api/cancellation/retention-response`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          policy_number: policyNumber,
          action: action
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to process response');
      }
      
      if (action === 'keep_coverage') {
        navigate(`/portal`);
        return;
      }
      
      if (action === 'cancel_at_term') {
        setEffectiveDate(policyInfo.expiration_date);
        setCancellationType('end_of_term');
      }
      
      setCurrentStep(STEPS.DISCLOSURES);
      
    } catch (err) {
      setError(err.message);
    } finally {
      setProcessing(false);
    }
  };

  const handleConfirmCancellation = async () => {
    try {
      setProcessing(true);
      setError(null);
      
      const response = await fetch(`${API_URL}/api/cancellation/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          policy_number: policyNumber,
          reason_code: selectedReason,
          reason_details: reasonDetails,
          effective_date: effectiveDate,
          cancellation_type: cancellationType,
          disclosures: {
            ...disclosures,
            acknowledged_at: new Date().toISOString()
          },
          retention_offered: true,
          retention_action: retentionAction || 'continue_cancellation'
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to process cancellation');
      }
      
      setCancellationResult(data);
      setCurrentStep(STEPS.COMPLETE);
      
    } catch (err) {
      setError(err.message);
    } finally {
      setProcessing(false);
    }
  };

  const handleNextStep = async () => {
    setError(null);
    
    switch (currentStep) {
      case STEPS.REASON:
        if (!selectedReason) {
          setError('Please select a reason for cancellation');
          return;
        }
        setCurrentStep(STEPS.TIMING);
        break;
        
      case STEPS.TIMING:
        const isValid = await validateDate();
        if (isValid) {
          await fetchRetentionOffer();
          setCurrentStep(STEPS.RETENTION);
        }
        break;
        
      case STEPS.RETENTION:
        // This is handled by handleRetentionResponse
        break;
        
      case STEPS.DISCLOSURES:
        if (!disclosures.coverage_ends_acknowledged || 
            !disclosures.no_future_coverage_acknowledged || 
            !disclosures.refund_terms_acknowledged) {
          setError('Please acknowledge all disclosures to proceed');
          return;
        }
        setCurrentStep(STEPS.CONFIRMATION);
        break;
        
      case STEPS.CONFIRMATION:
        await handleConfirmCancellation();
        break;
        
      default:
        break;
    }
  };

  const handlePrevStep = () => {
    switch (currentStep) {
      case STEPS.TIMING:
        setCurrentStep(STEPS.REASON);
        break;
      case STEPS.RETENTION:
        setCurrentStep(STEPS.TIMING);
        break;
      case STEPS.DISCLOSURES:
        setCurrentStep(STEPS.RETENTION);
        break;
      case STEPS.CONFIRMATION:
        setCurrentStep(STEPS.DISCLOSURES);
        break;
      default:
        break;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      // Format as MM/DD/YYYY
      return d.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return 'TBD';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: amount % 1 === 0 ? 0 : 2
    }).format(amount);
  };

  // Get today's date in YYYY-MM-DD format for min date
  const today = new Date().toISOString().split('T')[0];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#E8EEFF] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-10 w-10 animate-spin text-[#3B5BDB] mx-auto mb-4" />
          <p className="text-slate-600 font-medium">Loading policy information...</p>
        </div>
      </div>
    );
  }

  if (error && !policyInfo) {
    return (
      <div className="min-h-screen bg-[#E8EEFF] flex items-center justify-center p-4">
        <Card className="max-w-md w-full shadow-xl border-0 rounded-2xl">
          <CardContent className="pt-8 pb-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <XCircle className="h-8 w-8 text-red-500" />
              </div>
              <h2 className="text-xl font-bold text-slate-900 mb-2">Unable to Load Policy</h2>
              <p className="text-slate-600 mb-6">{error}</p>
              <Button onClick={() => navigate(`/portal`)} variant="outline" className="rounded-xl">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Return to Policy
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <PortalLayout
      pageTitle="Cancel Policy"
      policyNumber={policyNumber}
      userName={policyInfo?.policyholder?.first_name}
    >
      <div className="bg-[#E8EEFF] min-h-full py-8 md:py-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          
          {/* Progress Indicator */}
          {currentStep !== STEPS.COMPLETE && (
            <div className="mb-8">
              <div className="flex items-center justify-between text-sm text-slate-500 mb-2">
                <span className="font-medium">Cancel Policy</span>
                <span>
                  Step {Object.values(STEPS).indexOf(currentStep) + 1} of 5
                </span>
              </div>
              <div className="h-2 bg-white rounded-full overflow-hidden shadow-inner">
                <div 
                  className="h-full bg-red-500 transition-all duration-300 rounded-full"
                  style={{ 
                    width: `${((Object.values(STEPS).indexOf(currentStep) + 1) / 5) * 100}%` 
                  }}
                />
              </div>
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          {/* Step: Reason Selection */}
          {currentStep === STEPS.REASON && (
            <Card className="shadow-xl border-0 rounded-2xl">
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2 text-xl">
                  <AlertTriangle className="h-5 w-5 text-amber-500" />
                  Why are you cancelling?
                </CardTitle>
                <CardDescription>
                  Policy: {policyNumber} • {policyInfo?.vehicle_description}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <RadioGroup value={selectedReason} onValueChange={setSelectedReason}>
                  {reasons.map((reason) => (
                    <div 
                      key={reason.code}
                      className={`flex items-start space-x-3 p-4 rounded-xl border-2 transition-colors cursor-pointer ${
                        selectedReason === reason.code 
                          ? 'border-[#3B5BDB] bg-[#E8EEFF]' 
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                      onClick={() => setSelectedReason(reason.code)}
                    >
                      <RadioGroupItem value={reason.code} id={reason.code} className="mt-0.5" />
                      <div className="flex-1">
                        <Label htmlFor={reason.code} className="text-base font-medium cursor-pointer">
                          {reason.label}
                        </Label>
                        <p className="text-sm text-slate-500 mt-1">{reason.description}</p>
                      </div>
                    </div>
                  ))}
                </RadioGroup>

                {selectedReason === 'other' && (
                  <div className="mt-4">
                    <Label htmlFor="reason-details" className="text-slate-700 font-medium">Please provide details</Label>
                    <Textarea
                      id="reason-details"
                      value={reasonDetails}
                      onChange={(e) => setReasonDetails(e.target.value)}
                      placeholder="Tell us more about why you're cancelling..."
                      className="mt-2 rounded-xl border-slate-200 focus:border-[#3B5BDB] focus:ring-[#3B5BDB]/20"
                      rows={3}
                    />
                  </div>
                )}

                <div className="flex justify-end pt-4">
                  <Button onClick={handleNextStep} disabled={!selectedReason} className="bg-[#3B5BDB] hover:bg-[#2F4BC7] rounded-xl">
                    Continue
                    <ChevronRight className="h-4 w-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step: Timing Selection */}
          {currentStep === STEPS.TIMING && (
            <Card className="shadow-xl border-0 rounded-2xl">
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2 text-xl">
                  <CalendarIcon className="h-5 w-5 text-[#3B5BDB]" />
                  When should your cancellation take effect?
                </CardTitle>
                <CardDescription>
                  Coverage terminates at 12:01 AM on the selected date
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <Label htmlFor="effective-date" className="text-base text-slate-700 font-medium">Cancellation Effective Date</Label>
                  <div className="relative mt-2 max-w-full">
                    <CalendarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400 pointer-events-none z-10" />
                    <div 
                      className="w-full pl-10 pr-3 py-3 border border-slate-200 rounded-xl bg-white text-slate-900 text-base box-border cursor-pointer flex items-center justify-between"
                      onClick={() => document.getElementById('effective-date-hidden').showPicker()}
                    >
                      <span>{effectiveDate ? formatDate(effectiveDate) : 'Select a date'}</span>
                      <CalendarIcon className="h-5 w-5 text-slate-400" />
                    </div>
                    <input
                      type="date"
                      id="effective-date-hidden"
                      value={effectiveDate}
                      onChange={(e) => setEffectiveDate(e.target.value)}
                      min={today}
                      max={policyInfo?.expiration_date?.split('T')[0]}
                      className="absolute inset-0 opacity-0 w-full h-full cursor-pointer"
                    />
                  </div>
                  <p className="text-sm text-slate-500 mt-2">
                    Policy expires: {formatDate(policyInfo?.expiration_date)}
                  </p>
                </div>

                <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
                  <div className="flex items-start gap-3">
                    <Info className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-amber-800">Important</p>
                      <p className="text-sm text-amber-700 mt-1">
                        Backdating is not permitted. If you need coverage for a past incident, 
                        please contact us before cancelling.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex justify-between pt-4">
                  <Button variant="outline" onClick={handlePrevStep} className="rounded-xl border-slate-200">
                    <ChevronLeft className="h-4 w-4 mr-2" />
                    Back
                  </Button>
                  <Button onClick={handleNextStep} disabled={processing} className="bg-[#3B5BDB] hover:bg-[#2F4BC7] rounded-xl">
                    {processing ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Validating...
                      </>
                    ) : (
                      <>
                        Continue
                        <ChevronRight className="h-4 w-4 ml-2" />
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step: Retention Offer */}
          {currentStep === STEPS.RETENTION && retentionData && (
            <Card className="shadow-xl border-0 rounded-2xl">
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2 text-xl">
                  <Shield className="h-6 w-6 text-[#3B5BDB]" />
                  Before You Cancel
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Retention Message */}
                <div className="p-5 bg-[#E8EEFF] border border-[#3B5BDB]/20 rounded-xl">
                  <p className="text-[#2F4BC7] leading-relaxed font-medium">
                    {retentionData.retention_message}
                  </p>
                </div>

                {/* Refund Estimate */}
                {refundEstimate && (
                  <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl">
                    <p className="text-sm font-medium text-slate-700 mb-1">Estimated Refund</p>
                    <p className="text-2xl font-bold text-slate-900">
                      {refundEstimate.estimated_refund !== null 
                        ? formatCurrency(refundEstimate.estimated_refund)
                        : 'To be calculated'}
                    </p>
                    <p className="text-sm text-slate-500 mt-2">
                      {refundEstimate.refund_message}
                    </p>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="space-y-3 pt-4">
                  <Button 
                    className="w-full justify-center py-6 text-base bg-[#3B5BDB] hover:bg-[#2F4BC7] rounded-xl"
                    onClick={() => handleRetentionResponse('keep_coverage')}
                    disabled={processing}
                  >
                    <Shield className="h-5 w-5 mr-2" />
                    Keep My Coverage
                  </Button>
                  
                  <Button 
                    variant="outline"
                    className="w-full justify-center py-6 text-base rounded-xl border-slate-200"
                    onClick={() => handleRetentionResponse('continue_cancellation')}
                    disabled={processing}
                  >
                    Continue Cancellation
                    <ChevronRight className="h-5 w-5 ml-2" />
                  </Button>

                  <Button 
                    variant="ghost"
                    className="w-full justify-center text-slate-600 rounded-xl"
                    onClick={() => handleRetentionResponse('cancel_at_term')}
                    disabled={processing}
                  >
                    Cancel at End of Term ({formatDate(policyInfo?.expiration_date)})
                  </Button>
                </div>

                <div className="flex justify-start pt-2">
                  <Button variant="ghost" onClick={handlePrevStep} className="text-slate-500 rounded-xl">
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    Back
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step: Disclosures */}
          {currentStep === STEPS.DISCLOSURES && (
            <Card className="shadow-xl border-0 rounded-2xl">
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2 text-xl">
                  <AlertTriangle className="h-5 w-5 text-amber-500" />
                  Required Acknowledgements
                </CardTitle>
                <CardDescription>
                  Please review and acknowledge the following before proceeding
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div 
                  className={`p-4 rounded-xl border-2 transition-colors ${
                    disclosures.coverage_ends_acknowledged 
                      ? 'border-green-500 bg-green-50' 
                      : 'border-slate-200'
                  }`}
                >
                  <div className="flex items-start space-x-3">
                    <Checkbox
                      id="coverage-ends"
                      checked={disclosures.coverage_ends_acknowledged}
                      onCheckedChange={(checked) => 
                        setDisclosures(prev => ({ ...prev, coverage_ends_acknowledged: checked }))
                      }
                      className="mt-1 data-[state=checked]:bg-green-500 data-[state=checked]:border-green-500"
                    />
                    <div>
                      <Label htmlFor="coverage-ends" className="text-base font-medium cursor-pointer">
                        Coverage Termination
                      </Label>
                      <p className="text-sm text-slate-600 mt-1">
                        I understand that my Diminished Value Protection coverage will terminate 
                        at 12:01 AM on <strong>{formatDate(effectiveDate)}</strong>.
                      </p>
                    </div>
                  </div>
                </div>

                <div 
                  className={`p-4 rounded-xl border-2 transition-colors ${
                    disclosures.no_future_coverage_acknowledged 
                      ? 'border-green-500 bg-green-50' 
                      : 'border-slate-200'
                  }`}
                >
                  <div className="flex items-start space-x-3">
                    <Checkbox
                      id="no-future-coverage"
                      checked={disclosures.no_future_coverage_acknowledged}
                      onCheckedChange={(checked) => 
                        setDisclosures(prev => ({ ...prev, no_future_coverage_acknowledged: checked }))
                      }
                      className="mt-1 data-[state=checked]:bg-green-500 data-[state=checked]:border-green-500"
                    />
                    <div>
                      <Label htmlFor="no-future-coverage" className="text-base font-medium cursor-pointer">
                        No Future Coverage
                      </Label>
                      <p className="text-sm text-slate-600 mt-1">
                        I understand that no coverage applies to incidents occurring after 
                        the cancellation effective date. Any loss or damage after this date 
                        will not be covered under this policy.
                      </p>
                    </div>
                  </div>
                </div>

                <div 
                  className={`p-4 rounded-xl border-2 transition-colors ${
                    disclosures.refund_terms_acknowledged 
                      ? 'border-green-500 bg-green-50' 
                      : 'border-slate-200'
                  }`}
                >
                  <div className="flex items-start space-x-3">
                    <Checkbox
                      id="refund-terms"
                      checked={disclosures.refund_terms_acknowledged}
                      onCheckedChange={(checked) => 
                        setDisclosures(prev => ({ ...prev, refund_terms_acknowledged: checked }))
                      }
                      className="mt-1 data-[state=checked]:bg-green-500 data-[state=checked]:border-green-500"
                    />
                    <div>
                      <Label htmlFor="refund-terms" className="text-base font-medium cursor-pointer">
                        Refund Terms
                      </Label>
                      <p className="text-sm text-slate-600 mt-1">
                        I understand that any refund will be calculated according to the 
                        policy terms and applicable state law. The estimated refund amount 
                        may differ from the final amount.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex justify-between pt-6">
                  <Button variant="outline" onClick={handlePrevStep} className="rounded-xl border-slate-200">
                    <ChevronLeft className="h-4 w-4 mr-2" />
                    Back
                  </Button>
                  <Button 
                    onClick={handleNextStep}
                    disabled={
                      !disclosures.coverage_ends_acknowledged || 
                      !disclosures.no_future_coverage_acknowledged || 
                      !disclosures.refund_terms_acknowledged
                    }
                    className="bg-[#3B5BDB] hover:bg-[#2F4BC7] rounded-xl"
                  >
                    Continue
                    <ChevronRight className="h-4 w-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step: Final Confirmation */}
          {currentStep === STEPS.CONFIRMATION && (
            <Card className="border-red-200 shadow-xl rounded-2xl">
              <CardHeader className="bg-red-50 rounded-t-2xl border-b border-red-200 pb-4">
                <CardTitle className="flex items-center gap-2 text-red-700 text-xl">
                  <AlertTriangle className="h-5 w-5" />
                  Confirm Cancellation
                </CardTitle>
                <CardDescription className="text-red-600">
                  This action cannot be undone
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6 pt-6">
                {/* Summary */}
                <div className="space-y-4">
                  <div className="flex justify-between py-3 border-b border-slate-100">
                    <span className="text-slate-600">Policy Number</span>
                    <span className="font-medium font-mono">{policyNumber}</span>
                  </div>
                  <div className="flex justify-between py-3 border-b border-slate-100">
                    <span className="text-slate-600">Vehicle</span>
                    <span className="font-medium">{policyInfo?.vehicle_description}</span>
                  </div>
                  <div className="flex justify-between py-3 border-b border-slate-100">
                    <span className="text-slate-600">Cancellation Reason</span>
                    <span className="font-medium">
                      {reasons.find(r => r.code === selectedReason)?.label}
                    </span>
                  </div>
                  <div className="flex justify-between py-3 border-b border-slate-100">
                    <span className="text-slate-600">Effective Date</span>
                    <span className="font-medium text-red-600">{formatDate(effectiveDate)}</span>
                  </div>
                  {refundEstimate && (
                    <div className="flex justify-between py-3 border-b border-slate-100">
                      <span className="text-slate-600">Estimated Refund</span>
                      <span className="font-medium text-green-600">
                        {refundEstimate.estimated_refund !== null 
                          ? formatCurrency(refundEstimate.estimated_refund)
                          : 'To be calculated'}
                      </span>
                    </div>
                  )}
                </div>

                <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
                  <p className="text-sm text-red-700">
                    By clicking "Cancel My Policy", you confirm that you have read and 
                    understood all disclosures, and you authorize the cancellation of 
                    your Diminished Value Protection policy effective {formatDate(effectiveDate)}.
                  </p>
                </div>

                <div className="flex justify-between pt-4">
                  <Button variant="outline" onClick={handlePrevStep} className="rounded-xl border-slate-200">
                    <ChevronLeft className="h-4 w-4 mr-2" />
                    Back
                  </Button>
                  <Button 
                    variant="destructive"
                    onClick={handleNextStep}
                    disabled={processing}
                    className="bg-red-600 hover:bg-red-700 rounded-xl"
                  >
                    {processing ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        Cancel My Policy
                        <XCircle className="h-4 w-4 ml-2" />
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step: Completion */}
          {currentStep === STEPS.COMPLETE && cancellationResult && (
            <Card className="shadow-xl border-0 rounded-2xl">
              <CardContent className="pt-8 pb-8">
                <div className="text-center">
                  <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="h-8 w-8 text-green-500" />
                  </div>
                  
                  <h2 className="text-2xl font-bold text-slate-900 mb-2">
                    Cancellation <span className="text-[#3B5BDB]">Complete</span>
                  </h2>
                  <p className="text-slate-600 mb-8">
                    Your policy has been cancelled as requested.
                  </p>

                  <div className="bg-slate-50 rounded-xl p-6 text-left max-w-md mx-auto mb-8">
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-slate-600">Cancellation ID</span>
                        <span className="font-mono font-medium">{cancellationResult.cancellation_id}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600">Policy Number</span>
                        <span className="font-medium">{cancellationResult.policy_number}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600">Effective Date</span>
                        <span className="font-medium">{formatDate(cancellationResult.effective_date)}</span>
                      </div>
                      {refundEstimate?.estimated_refund !== null && (
                        <div className="flex justify-between pt-3 border-t border-slate-200">
                          <span className="text-slate-600">Estimated Refund</span>
                          <span className="font-medium text-green-600">
                            {formatCurrency(refundEstimate.estimated_refund)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {cancellationResult.confirmation_email_sent && (
                    <p className="text-sm text-slate-500 mb-8">
                      A confirmation email has been sent to your email address.
                    </p>
                  )}

                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Button 
                      variant="outline"
                      onClick={() => navigate('/')}
                      className="rounded-xl border-slate-200"
                    >
                      Return to Home
                    </Button>
                    <Button 
                      onClick={() => window.open('https://secure.losspay.com', '_blank')}
                      className="bg-[#3B5BDB] hover:bg-[#2F4BC7] rounded-xl"
                    >
                      Get New Coverage
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </PortalLayout>
  );
}
